// src/pages/SentNotificationsHistory.js

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, orderBy, onSnapshot, collectionGroup } from 'firebase/firestore';
import {
  Container, Typography, Box, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Collapse, IconButton, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import format from 'date-fns/format';

function SentNotificationRow({ notification }) {
  const [open, setOpen] = useState(false);

  const formatRecipientDetails = (type, details) => {
    if (!details || details.length === 0) return 'N/A';
    if (type === 'roles') return details.join(', ');
    if (type === 'groups') return details.join(', ');
    if (type === 'users') return details.join(', ');
    return details.join(', ');
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {notification.title}
        </TableCell>
        <TableCell>{notification.type}</TableCell>
        <TableCell>{notification.senderName}</TableCell>
        <TableCell>{format(notification.timestamp.toDate(), 'MMM d, yyyy h:mm a')}</TableCell>
        <TableCell align="right">
          <Chip
            label={`${notification.fcmSuccessCount} / ${notification.fcmSuccessCount + notification.fcmFailureCount}`}
            color={notification.fcmFailureCount > 0 ? 'warning' : 'success'}
            size="small"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Details
              </Typography>
              <Typography variant="body2">
                <strong>Message:</strong> {notification.bodySnippet}
              </Typography>
              <Typography variant="body2">
                <strong>Sent To:</strong> {notification.recipientType}
              </Typography>
              <Typography variant="body2">
                <strong>Recipient IDs:</strong> {formatRecipientDetails(notification.recipientType, notification.recipientDetails)}
              </Typography>
              {notification.relatedDocId && (
                <Typography variant="body2">
                  <strong>Related Doc ID:</strong> {notification.relatedDocId}
                  {notification.relatedDocPath && ` (${notification.relatedDocPath})`}
                </Typography>
              )}
              {notification.fcmErrorDetails && notification.fcmErrorDetails.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="error">
                    <strong>FCM Errors:</strong>
                  </Typography>
                  <ul>
                    {notification.fcmErrorDetails.map((error, index) => (
                      <li key={index}><Typography variant="caption" color="error">{error.error}</Typography></li>
                    ))}
                  </ul>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function SentNotificationsHistory() {
  const { userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [churches, setChurches] = useState([]); // State to hold church data
  const [loading, setLoading] = useState(true);

  const canViewHistory = userProfile?.role === 'Church Administrator' ||
                         userProfile?.role === 'Super Administrator';
  const isSuperAdmin = userProfile?.role === 'Super Administrator';

  // Effect to fetch all churches if user is a Super Admin
  useEffect(() => {
    if (isSuperAdmin) {
      const churchesQuery = query(collection(db, 'churches'), orderBy('name', 'asc'));
      const unsubscribe = onSnapshot(churchesQuery, (snapshot) => {
        setChurches(snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name })));
      });
      return () => unsubscribe();
    }
  }, [isSuperAdmin]);


  useEffect(() => {
    if (!canViewHistory) {
      setLoading(false);
      return;
    }

    let q;
    if (isSuperAdmin) {
      q = query(collectionGroup(db, 'sentNotifications'), orderBy('timestamp', 'desc'));
    } else if (userProfile?.churchId) {
      q = query(collection(db, 'churches', userProfile.churchId, 'sentNotifications'), orderBy('timestamp', 'desc'));
    } else {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedNotifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setNotifications(fetchedNotifications);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching sent notifications:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userProfile, canViewHistory, isSuperAdmin]);

  // MODIFIED: Memoized grouping of notifications by church for Super Admins
  const groupedNotifications = useMemo(() => {
    if (!isSuperAdmin) return null;

    const groups = {};
    notifications.forEach(notification => {
      const church = churches.find(c => c.id === notification.churchId);
      // MODIFIED: Enhanced fallback to clearly distinguish known vs. unknown churches
      const groupLabel = church 
                         ? church.name 
                         : (notification.churchId 
                            ? `Unknown Church (ID: ${notification.churchId})` 
                            : 'Unknown Church');

      if (!groups[groupLabel]) { // Use the new groupLabel as the key
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(notification);
    });
    return groups;
  }, [notifications, churches, isSuperAdmin]);


  if (loading) {
    return <CircularProgress />;
  }

  if (!canViewHistory) {
    return (
      <Container maxWidth="md">
        <Typography variant="h6" color="error">
          You do not have permission to view sent notification history.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>Sent Notification History</Typography>
      
      {/* Render accordions for Super Admin, or a single table for Church Admin */}
      {isSuperAdmin ? (
        Object.keys(groupedNotifications).sort().map(churchName => ( // Iterate through the generated keys
          <Accordion key={churchName} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{churchName} ({groupedNotifications[churchName].length})</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <TableContainer component={Paper} elevation={0}>
                <Table aria-label={`${churchName} sent notifications table`}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Sender</TableCell>
                      <TableCell>Timestamp</TableCell>
                      <TableCell align="right">Status (Success/Total)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedNotifications[churchName].map((notification) => (
                      <SentNotificationRow key={notification.id} notification={notification} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="sent notifications table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Sender</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell align="right">Status (Success/Total)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No notifications sent yet.</TableCell>
                </TableRow>
              ) : (
                notifications.map((notification) => (
                  <SentNotificationRow key={notification.id} notification={notification} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default SentNotificationsHistory;